/**
 * TOUR ARCADE (`/games`) — trois mini-jeux 100 % hors-ligne.
 *
 * WHY THIS PAGE EXISTS (décision user 2026-11-04) : l'onglet mobile `Shows` a
 * été remplacé par `🕹️ Games` — « il nous manque un truc fun pour tuer le
 * temps dans les files d'attente d'aéroports ». La page devient donc l'onglet
 * n°2 de la barre mobile, et reste joignable sur desktop (sidebar + palette ⌘K).
 *
 * RÈGLES DE CE FICHIER (elles tiennent tout le lot) :
 *  - ZÉRO dépendance externe : les trois jeux sont du Canvas 2D et du DOM. Un
 *    aéroport sans réseau n'a pas de CDN — si un asset manque, le jeu ne doit
 *    pas manquer avec lui. Aucune requête réseau n'est faite ici.
 *  - Chaque jeu est AUTO-CONTENU et monté UNIQUEMENT quand son onglet est actif
 *    (les boucles et les écouteurs clavier s'arrêtent en même temps que le jeu).
 *  - Tactile D'ABORD : manettes à boutons larges (≥48 px, `touch-action:
 *    manipulation`) + gestes directement sur le canvas. Le clavier est un
 *    bonus desktop, jamais le seul moyen de jouer.
 *  - Le meilleur score vit dans `localStorage` (`bandz.games.high.<jeu>`) et ne
 *    baisse JAMAIS : une partie ratée n'efface pas le record.
 *  - UI 100 % anglais via i18n (`games.*`) : aucun libellé en dur.
 *
 * RÉFÉRENCE DU LOT : `bandz-frontend/references/ui-roadcase-redesign-2026.md`
 * (section LOT TOUR-ARCADE-GAMES).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TouchEvent as ReactTouchEvent } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';
import BandzBeatz from '../components/BandzBeatz';
import { StageBreakerGame, TourbusRunnerGame, KaossSynthPanel } from '../components/ArcadeExtras';
import { TunerMetronomePanel, DbMeterRtaPanel, FreqHunterGame } from '../components/StageAudioTools';
import { LdToolboxPanel } from '../components/LdToolbox';
import {
  ScoreBar,
  StageOverlay,
  clamp,
  useGameStatus,
  useHighScore as useHighScoreKey,
} from '../components/gameKit';
import { withPageIcon } from '../lib/uiIcons';
import './GamesPage.css';

type GameId = 'snake' | 'tetris' | 'pong';
/** Jeux & synthés historiques (arcade « classique »). */
type ArcadeGameTab = GameId | 'beatz' | 'breaker' | 'runner' | 'kaoss';
/** Outils de scène (audio & lumière) — même page, autre famille. */
type StageToolTab = 'tuner' | 'rta' | 'freqhunter' | 'ld';
/** Tout ce que la page peut afficher : un seul module monté à la fois. */
type ArcadeTab = ArcadeGameTab | StageToolTab;

/* ====================================================================== */
/*  High score — un seul endroit écrit dans localStorage                   */
/* ====================================================================== */

/** Une clé par jeu ; le nom est un CONTRAT (les tests et le support y lisent). */
const HIGH_KEYS: Record<GameId, string> = {
  snake: 'bandz.games.high.snake',
  tetris: 'bandz.games.high.tetris',
  pong: 'bandz.games.high.pong',
};


/**
 * Record typé par jeu : la clé est un CONTRAT (`HIGH_KEYS`), la mécanique de
 * lecture/écriture vit dans `gameKit` (une seule implémentation pour 11 modules).
 */
function useHighScore(game: GameId): [number, (value: number) => void] {
  return useHighScoreKey(HIGH_KEYS[game]);
}


/* ====================================================================== */
/*  🐍 Cable Snake                                                        */
/* ====================================================================== */

const SNAKE_N = 20;
const SNAKE_CELL = 16;
const SNAKE_BASE_MS = 165;
const SNAKE_MIN_MS = 80;

interface Vec {
  x: number;
  y: number;
}

const SNAKE_DIRS: Record<string, Vec> = {
  arrowup: { x: 0, y: -1 },
  w: { x: 0, y: -1 },
  arrowdown: { x: 0, y: 1 },
  s: { x: 0, y: 1 },
  arrowleft: { x: -1, y: 0 },
  a: { x: -1, y: 0 },
  arrowright: { x: 1, y: 0 },
  d: { x: 1, y: 0 },
};

function SnakeGame() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [score, setScore] = useState(0);
  const [best, bumpBest] = useHighScore('snake');
  const { status, statusRef, setStatus } = useGameStatus();

  const snakeRef = useRef<Vec[]>([]);
  const dirRef = useRef<Vec>({ x: 1, y: 0 });
  const queueRef = useRef<Vec[]>([]);
  const foodRef = useRef<Vec>({ x: 15, y: 10 });
  const speedRef = useRef(SNAKE_BASE_MS);
  const accRef = useRef(0);
  const scoreRef = useRef(0);
  const touchRef = useRef<Vec | null>(null);

  const draw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const size = cv.width;

    ctx.fillStyle = '#1b1915';
    ctx.fillRect(0, 0, size, size);

    // Grille discrète : elle donne l'échelle d'une case sans distraire.
    ctx.strokeStyle = 'rgba(234, 231, 221, 0.07)';
    ctx.lineWidth = 1;
    for (let i = 1; i < SNAKE_N; i += 1) {
      const p = i * SNAKE_CELL + 0.5;
      ctx.beginPath();
      ctx.moveTo(p, 0);
      ctx.lineTo(p, size);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, p);
      ctx.lineTo(size, p);
      ctx.stroke();
    }

    // La prise (médiator) — vert signal, jamais confondu avec le serpent.
    const food = foodRef.current;
    ctx.fillStyle = '#00d47e';
    ctx.beginPath();
    ctx.arc(
      food.x * SNAKE_CELL + SNAKE_CELL / 2,
      food.y * SNAKE_CELL + SNAKE_CELL / 2,
      SNAKE_CELL * 0.33,
      0,
      Math.PI * 2,
    );
    ctx.fill();

    // Le câble : tête ambre clair, corps ambre Roadcase.
    snakeRef.current.forEach((seg, i) => {
      ctx.fillStyle = i === 0 ? '#f5c469' : '#e0a44c';
      ctx.fillRect(seg.x * SNAKE_CELL + 1, seg.y * SNAKE_CELL + 1, SNAKE_CELL - 2, SNAKE_CELL - 2);
    });
  }, []);

  const placeFood = useCallback(() => {
    const free: Vec[] = [];
    for (let y = 0; y < SNAKE_N; y += 1) {
      for (let x = 0; x < SNAKE_N; x += 1) {
        if (!snakeRef.current.some((s) => s.x === x && s.y === y)) free.push({ x, y });
      }
    }
    foodRef.current = free.length > 0 ? free[Math.floor(Math.random() * free.length)] : { x: 0, y: 0 };
  }, []);

  const reset = useCallback(() => {
    const mid = Math.floor(SNAKE_N / 2);
    snakeRef.current = [
      { x: mid, y: mid },
      { x: mid - 1, y: mid },
      { x: mid - 2, y: mid },
    ];
    dirRef.current = { x: 1, y: 0 };
    queueRef.current = [];
    speedRef.current = SNAKE_BASE_MS;
    accRef.current = 0;
    scoreRef.current = 0;
    setScore(0);
    placeFood();
    draw();
  }, [draw, placeFood]);

  const endGame = useCallback(() => {
    setStatus('over');
    bumpBest(scoreRef.current);
    draw();
  }, [bumpBest, draw, setStatus]);

  const move = useCallback(() => {
    const queued = queueRef.current.shift();
    const current = dirRef.current;
    const dir =
      queued && !(queued.x === -current.x && queued.y === -current.y) ? queued : current;
    dirRef.current = dir;

    const snake = snakeRef.current;
    const head = snake[0];
    const next: Vec = { x: head.x + dir.x, y: head.y + dir.y };

    // Murs : mourir au bord est la règle classique (pas de téléportation).
    if (next.x < 0 || next.y < 0 || next.x >= SNAKE_N || next.y >= SNAKE_N) {
      endGame();
      return;
    }
    if (snake.some((s) => s.x === next.x && s.y === next.y)) {
      endGame();
      return;
    }

    snake.unshift(next);
    const food = foodRef.current;
    if (next.x === food.x && next.y === food.y) {
      scoreRef.current += 1;
      setScore(scoreRef.current);
      // Accélération douce : +1 prise = un peu plus rapide, plancher 80 ms.
      speedRef.current = Math.max(SNAKE_MIN_MS, speedRef.current - 5);
      placeFood();
    } else {
      snake.pop();
    }
    draw();
  }, [draw, endGame, placeFood]);

  const start = useCallback(() => {
    if (statusRef.current === 'paused') {
      setStatus('running');
      return;
    }
    reset();
    setStatus('running');
  }, [reset, setStatus, statusRef]);

  const toggle = useCallback(() => {
    if (statusRef.current === 'running') setStatus('paused');
    else start();
  }, [setStatus, start, statusRef]);

  const enqueue = useCallback((dir: Vec) => {
    const last = queueRef.current.length > 0 ? queueRef.current[queueRef.current.length - 1] : dirRef.current;
    // Un demi-tour instantané reviendrait à manger sa propre tête : on l'ignore.
    if (dir.x === -last.x && dir.y === -last.y) return;
    if (dir.x === last.x && dir.y === last.y) return;
    if (queueRef.current.length < 3) queueRef.current.push(dir);
  }, []);

  const press = useCallback(
    (dir: Vec) => {
      enqueue(dir);
      if (statusRef.current !== 'running') start();
    },
    [enqueue, start, statusRef],
  );

  // Boucle : intervalle fixe (50 ms) + accumulateur, pour que la vitesse puisse
  // changer SANS recréer le timer à chaque prise.
  useEffect(() => {
    reset();
    const id = window.setInterval(() => {
      if (statusRef.current !== 'running') return;
      accRef.current += 50;
      let guard = 0;
      while (accRef.current >= speedRef.current && guard < 4) {
        accRef.current -= speedRef.current;
        guard += 1;
        move();
        if (statusRef.current !== 'running') break;
      }
    }, 50);
    return () => window.clearInterval(id);
  }, [move, reset, statusRef]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const dir = SNAKE_DIRS[event.key.toLowerCase()];
      if (dir) {
        event.preventDefault();
        press(dir);
        return;
      }
      if (event.key === ' ') {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press, toggle]);

  const onTouchStart = (event: ReactTouchEvent<HTMLCanvasElement>) => {
    const touch = event.touches[0];
    touchRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const onTouchEnd = (event: ReactTouchEvent<HTMLCanvasElement>) => {
    const from = touchRef.current;
    touchRef.current = null;
    if (!from) return;
    const touch = event.changedTouches[0];
    const dx = touch.clientX - from.x;
    const dy = touch.clientY - from.y;
    if (Math.abs(dx) < 18 && Math.abs(dy) < 18) return;
    press(
      Math.abs(dx) > Math.abs(dy)
        ? { x: dx > 0 ? 1 : -1, y: 0 }
        : { x: 0, y: dy > 0 ? 1 : -1 },
    );
  };

  return (
    <section className="game-panel" data-game="snake">
      <ScoreBar score={score} best={best} status={status} onToggle={toggle} onRestart={start} />

      <div className="game-stage">
        <canvas
          ref={canvasRef}
          width={320}
          height={320}
          className="game-canvas game-canvas-snake"
          role="img"
          aria-label={t('games.snake.title')}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        />
        {status === 'idle' && (
          <StageOverlay tone="info">
            <p className="game-overlay-title">{t('games.snake.title')}</p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.start')}
            </button>
          </StageOverlay>
        )}
        {status === 'paused' && (
          <StageOverlay tone="info">
            <p>{t('games.paused')}</p>
          </StageOverlay>
        )}
        {status === 'over' && (
          <StageOverlay tone="over">
            <p className="game-overlay-title">{t('games.gameOver')}</p>
            <p>
              {t('games.score')} : {score}
            </p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.restart')}
            </button>
          </StageOverlay>
        )}
      </div>

      <p className="game-desc">{t('games.snake.desc')}</p>

      {/* D-Pad : quatre boutons ≥ 48 px, jouables à une main dans la file. */}
      <div className="game-pad game-pad-dpad" role="group" aria-label={t('games.snake.title')}>
        <button type="button" className="game-ctl game-ctl-up" data-game-key="up" onClick={() => press({ x: 0, y: -1 })}>
          <span aria-hidden="true">▲</span>
        </button>
        <button type="button" className="game-ctl game-ctl-left" data-game-key="left" onClick={() => press({ x: -1, y: 0 })}>
          <span aria-hidden="true">◀</span>
        </button>
        <button type="button" className="game-ctl game-ctl-right" data-game-key="right" onClick={() => press({ x: 1, y: 0 })}>
          <span aria-hidden="true">▶</span>
        </button>
        <button type="button" className="game-ctl game-ctl-down" data-game-key="down" onClick={() => press({ x: 0, y: 1 })}>
          <span aria-hidden="true">▼</span>
        </button>
      </div>

      <p className="game-hint">{t('games.controlsHint')}</p>
    </section>
  );
}

/* ====================================================================== */
/*  🧱 Roadcase Tetris                                                    */
/* ====================================================================== */

const T_COLS = 10;
const T_ROWS = 20;
const T_CW = 24;
const T_CH = 22;

type Matrix = number[][];
type Board = (string | null)[][];

interface Piece {
  shape: Matrix;
  color: string;
  x: number;
  y: number;
}

const TETROMINOES: ReadonlyArray<{ shape: Matrix; color: string }> = [
  { shape: [[1, 1, 1, 1]], color: '#5ad1e6' }, // I
  { shape: [[1, 1], [1, 1]], color: '#f5c469' }, // O
  { shape: [[0, 1, 0], [1, 1, 1]], color: '#c084fc' }, // T
  { shape: [[0, 1, 1], [1, 1, 0]], color: '#00d47e' }, // S
  { shape: [[1, 1, 0], [0, 1, 1]], color: '#e06c6c' }, // Z
  { shape: [[1, 0, 0], [1, 1, 1]], color: '#6c8ce0' }, // J
  { shape: [[0, 0, 1], [1, 1, 1]], color: '#e0a44c' }, // L
];

function rotateCW(shape: Matrix): Matrix {
  const rows = shape.length;
  const cols = shape[0].length;
  const out: Matrix = Array.from({ length: cols }, () => Array<number>(rows).fill(0));
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) out[c][rows - 1 - r] = shape[r][c];
  }
  return out;
}

function emptyBoard(): Board {
  return Array.from({ length: T_ROWS }, () => Array<string | null>(T_COLS).fill(null));
}

function collides(board: Board, shape: Matrix, px: number, py: number): boolean {
  for (let r = 0; r < shape.length; r += 1) {
    for (let c = 0; c < shape[r].length; c += 1) {
      if (!shape[r][c]) continue;
      const x = px + c;
      const y = py + r;
      if (x < 0 || x >= T_COLS || y >= T_ROWS) return true;
      if (y >= 0 && board[y][x] !== null) return true;
    }
  }
  return false;
}

function spawnPiece(): Piece {
  const def = TETROMINOES[Math.floor(Math.random() * TETROMINOES.length)];
  return {
    shape: def.shape.map((row) => [...row]),
    color: def.color,
    x: Math.floor((T_COLS - def.shape[0].length) / 2),
    y: 0,
  };
}

/** Barème classique : 1/2/3/4 lignes = 100/300/500/800. */
const LINE_SCORES = [0, 100, 300, 500, 800];

function gravityMs(level: number): number {
  return Math.max(120, 760 - (level - 1) * 70);
}

function TetrisGame() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [score, setScore] = useState(0);
  const [best, bumpBest] = useHighScore('tetris');
  const [level, setLevel] = useState(1);
  const { status, statusRef, setStatus } = useGameStatus();

  const boardRef = useRef<Board>(emptyBoard());
  const pieceRef = useRef<Piece>(spawnPiece());
  const scoreRef = useRef(0);
  const linesRef = useRef(0);
  const levelRef = useRef(1);
  const accRef = useRef(0);

  const draw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#1b1915';
    ctx.fillRect(0, 0, cv.width, cv.height);

    // Grille de fond.
    ctx.strokeStyle = 'rgba(234, 231, 221, 0.06)';
    ctx.lineWidth = 1;
    for (let c = 1; c < T_COLS; c += 1) {
      const x = c * T_CW + 0.5;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, cv.height);
      ctx.stroke();
    }
    for (let r = 1; r < T_ROWS; r += 1) {
      const y = r * T_CH + 0.5;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(cv.width, y);
      ctx.stroke();
    }

    const cell = (x: number, y: number, color: string) => {
      ctx.fillStyle = color;
      ctx.fillRect(x * T_CW + 1, y * T_CH + 1, T_CW - 2, T_CH - 2);
    };

    boardRef.current.forEach((row, y) => {
      row.forEach((color, x) => {
        if (color) cell(x, y, color);
      });
    });

    const piece = pieceRef.current;
    piece.shape.forEach((row, r) => {
      row.forEach((filled, c) => {
        if (filled) cell(piece.x + c, piece.y + r, piece.color);
      });
    });
  }, []);

  const endGame = useCallback(() => {
    setStatus('over');
    bumpBest(scoreRef.current);
    draw();
  }, [bumpBest, draw, setStatus]);

  const lockPiece = useCallback(() => {
    const piece = pieceRef.current;
    const board = boardRef.current;
    piece.shape.forEach((row, r) => {
      row.forEach((filled, c) => {
        if (!filled) return;
        const y = piece.y + r;
        const x = piece.x + c;
        if (y >= 0 && y < T_ROWS) board[y][x] = piece.color;
      });
    });

    // Lignes complètes → score puis tassement.
    let cleared = 0;
    for (let r = T_ROWS - 1; r >= 0; r -= 1) {
      if (board[r].every((v) => v !== null)) {
        board.splice(r, 1);
        board.unshift(Array<string | null>(T_COLS).fill(null));
        cleared += 1;
        r += 1; // la ligne suivante vient de descendre à cet index
      }
    }
    if (cleared > 0) {
      scoreRef.current += LINE_SCORES[cleared] ?? 800;
      linesRef.current += cleared;
      const nextLevel = Math.floor(linesRef.current / 10) + 1;
      if (nextLevel !== levelRef.current) {
        levelRef.current = nextLevel;
        setLevel(nextLevel);
      }
      setScore(scoreRef.current);
    }

    pieceRef.current = spawnPiece();
    if (collides(board, pieceRef.current.shape, pieceRef.current.x, pieceRef.current.y)) {
      endGame();
      return;
    }
    draw();
  }, [draw, endGame]);

  const gravity = useCallback(() => {
    const piece = pieceRef.current;
    if (!collides(boardRef.current, piece.shape, piece.x, piece.y + 1)) {
      piece.y += 1;
      draw();
    } else {
      lockPiece();
    }
  }, [draw, lockPiece]);

  const softDrop = useCallback(() => {
    if (statusRef.current !== 'running') return;
    gravity();
  }, [gravity, statusRef]);

  const hardDrop = useCallback(() => {
    if (statusRef.current !== 'running') return;
    const piece = pieceRef.current;
    let guard = 0;
    while (!collides(boardRef.current, piece.shape, piece.x, piece.y + 1) && guard < T_ROWS + 2) {
      piece.y += 1;
      guard += 1;
    }
    lockPiece();
  }, [lockPiece, statusRef]);

  const moveBy = useCallback(
    (dx: number) => {
      if (statusRef.current !== 'running') return;
      const piece = pieceRef.current;
      if (!collides(boardRef.current, piece.shape, piece.x + dx, piece.y)) {
        piece.x += dx;
        draw();
      }
    },
    [draw, statusRef],
  );

  const rotate = useCallback(() => {
    if (statusRef.current !== 'running') return;
    const piece = pieceRef.current;
    const rotated = rotateCW(piece.shape);
    // Anti-collision murale simple : on décale d'une case vers l'intérieur
    // (le « wall kick » minimal qui évite un blocage contre le bord).
    for (const kick of [0, -1, 1, -2, 2]) {
      if (!collides(boardRef.current, rotated, piece.x + kick, piece.y)) {
        piece.shape = rotated;
        piece.x += kick;
        draw();
        return;
      }
    }
  }, [draw, statusRef]);

  const reset = useCallback(() => {
    boardRef.current = emptyBoard();
    pieceRef.current = spawnPiece();
    scoreRef.current = 0;
    linesRef.current = 0;
    levelRef.current = 1;
    accRef.current = 0;
    setScore(0);
    setLevel(1);
    draw();
  }, [draw]);

  const start = useCallback(() => {
    if (statusRef.current === 'paused') {
      setStatus('running');
      return;
    }
    reset();
    setStatus('running');
  }, [reset, setStatus, statusRef]);

  const toggle = useCallback(() => {
    if (statusRef.current === 'running') setStatus('paused');
    else start();
  }, [setStatus, start, statusRef]);

  useEffect(() => {
    reset();
    const id = window.setInterval(() => {
      if (statusRef.current !== 'running') return;
      accRef.current += 50;
      const step = gravityMs(levelRef.current);
      if (accRef.current >= step) {
        accRef.current = 0;
        gravity();
      }
    }, 50);
    return () => window.clearInterval(id);
  }, [gravity, reset, statusRef]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key === 'arrowleft' || key === 'a') {
        event.preventDefault();
        moveBy(-1);
      } else if (key === 'arrowright' || key === 'd') {
        event.preventDefault();
        moveBy(1);
      } else if (key === 'arrowdown' || key === 's') {
        event.preventDefault();
        softDrop();
      } else if (key === 'arrowup' || key === 'w') {
        event.preventDefault();
        rotate();
      } else if (key === ' ') {
        event.preventDefault();
        hardDrop();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hardDrop, moveBy, rotate, softDrop]);

  return (
    <section className="game-panel" data-game="tetris">
      <ScoreBar
        score={score}
        best={best}
        status={status}
        onToggle={toggle}
        onRestart={start}
        extra={`${t('games.level')} ${level}`}
      />

      <div className="game-stage game-stage-tetris">
        <canvas
          ref={canvasRef}
          width={T_COLS * T_CW}
          height={T_ROWS * T_CH}
          className="game-canvas game-canvas-tetris"
          role="img"
          aria-label={t('games.tetris.title')}
        />
        {status === 'idle' && (
          <StageOverlay tone="info">
            <p className="game-overlay-title">{t('games.tetris.title')}</p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.start')}
            </button>
          </StageOverlay>
        )}
        {status === 'paused' && (
          <StageOverlay tone="info">
            <p>{t('games.paused')}</p>
          </StageOverlay>
        )}
        {status === 'over' && (
          <StageOverlay tone="over">
            <p className="game-overlay-title">{t('games.gameOver')}</p>
            <p>
              {t('games.score')} : {score}
            </p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.restart')}
            </button>
          </StageOverlay>
        )}
      </div>

      <p className="game-desc">{t('games.tetris.desc')}</p>

      <div className="game-pad game-pad-tetris" role="group" aria-label={t('games.tetris.title')}>
        <button type="button" className="game-ctl" data-game-key="left" onClick={() => moveBy(-1)}>
          <span aria-hidden="true">◀</span>
        </button>
        <button type="button" className="game-ctl game-ctl-wide" data-game-key="rotate" onClick={rotate}>
          <span aria-hidden="true">⟳</span> {t('games.rotate')}
        </button>
        <button type="button" className="game-ctl" data-game-key="right" onClick={() => moveBy(1)}>
          <span aria-hidden="true">▶</span>
        </button>
        <button type="button" className="game-ctl game-ctl-wide" data-game-key="soft" onClick={softDrop}>
          <span aria-hidden="true">▼</span> {t('games.softDrop')}
        </button>
        <button type="button" className="game-ctl game-ctl-wide" data-game-key="drop" onClick={hardDrop}>
          <span aria-hidden="true">⤓</span> {t('games.drop')}
        </button>
      </div>

      <p className="game-hint">{t('games.controlsHint')}</p>
    </section>
  );
}

/* ====================================================================== */
/*  🏓 Soundcheck Pong                                                    */
/* ====================================================================== */

const P_W = 340;
const P_H = 260;
const PADDLE_W = 70;
const PADDLE_H = 9;
const PLAYER_Y = P_H - 18;
const AI_Y = 9;
const BALL_R = 6;
const PONG_TARGET = 7;
const PONG_TICK = 16;
const SERVE_FREEZE = 36; // ticks ≈ 0,6 s avant que la balle reparte

function PongGame() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [rally, setRally] = useState(0);
  const [best, bumpBest] = useHighScore('pong');
  const [you, setYou] = useState(0);
  const [foh, setFoh] = useState(0);
  const { status, statusRef, setStatus } = useGameStatus();

  const playerXRef = useRef(P_W / 2 - PADDLE_W / 2);
  const aiXRef = useRef(P_W / 2 - PADDLE_W / 2);
  const ballRef = useRef({ x: P_W / 2, y: P_H / 2, vx: 0.6, vy: -3.2 });
  const rallyRef = useRef(0);
  const freezeRef = useRef(SERVE_FREEZE);
  const youRef = useRef(0);
  const fohRef = useRef(0);
  const totalRef = useRef(0);

  const tRef = useRef(t);
  tRef.current = t;

  const draw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#1b1915';
    ctx.fillRect(0, 0, P_W, P_H);

    // Ligne médiane : le « soundcheck » du terrain.
    ctx.strokeStyle = 'rgba(234, 231, 221, 0.18)';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 7]);
    ctx.beginPath();
    ctx.moveTo(0, P_H / 2);
    ctx.lineTo(P_W, P_H / 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // Score au tableau : You vs FOH.
    ctx.fillStyle = 'rgba(234, 231, 221, 0.75)';
    ctx.font = '600 12px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`${tRef.current('games.you')} ${youRef.current}  ·  ${tRef.current('games.foh')} ${fohRef.current}`, P_W / 2, P_H / 2 - 10);

    // Raquette IA (haut) — vert signal, raquette joueur (bas) — ambre.
    ctx.fillStyle = '#00d47e';
    ctx.fillRect(aiXRef.current, AI_Y, PADDLE_W, PADDLE_H);
    ctx.fillStyle = '#e0a44c';
    ctx.fillRect(playerXRef.current, PLAYER_Y, PADDLE_W, PADDLE_H);

    const ball = ballRef.current;
    ctx.fillStyle = '#f5c469';
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, BALL_R, 0, Math.PI * 2);
    ctx.fill();
  }, []);

  const serve = useCallback((towardPlayer: boolean) => {
    ballRef.current = {
      x: P_W / 2,
      y: P_H / 2,
      vx: (Math.random() > 0.5 ? 1 : -1) * 1.4,
      vy: towardPlayer ? 3.2 : -3.2,
    };
    freezeRef.current = SERVE_FREEZE;
  }, []);

  const finishMatch = useCallback(
    (won: boolean) => {
      setStatus(won ? 'won' : 'over');
      bumpBest(totalRef.current);
      draw();
    },
    [bumpBest, draw, setStatus],
  );

  const point = useCallback(
    (toPlayer: boolean) => {
      if (toPlayer) {
        youRef.current += 1;
        setYou(youRef.current);
      } else {
        fohRef.current += 1;
        setFoh(fohRef.current);
      }
      rallyRef.current = 0;
      setRally(0);
      if (youRef.current >= PONG_TARGET) {
        finishMatch(true);
        return;
      }
      if (fohRef.current >= PONG_TARGET) {
        finishMatch(false);
        return;
      }
      serve(!toPlayer);
      draw();
    },
    [draw, finishMatch, serve],
  );

  const step = useCallback(() => {
    if (statusRef.current !== 'running') return;
    if (freezeRef.current > 0) {
      freezeRef.current -= 1;
      draw();
      return;
    }

    const ball = ballRef.current;

    // IA FOH : suit la balle, plus lentement que la raquette du joueur.
    const aiTarget = clamp(ball.x - PADDLE_W / 2, 0, P_W - PADDLE_W);
    const aiSpeed = 2.4 + Math.min(1.6, rallyRef.current * 0.05);
    aiXRef.current = clamp(
      aiXRef.current + clamp(aiTarget - aiXRef.current, -aiSpeed, aiSpeed),
      0,
      P_W - PADDLE_W,
    );

    ball.x += ball.vx;
    ball.y += ball.vy;

    // Murs latéraux.
    if (ball.x < BALL_R) {
      ball.x = BALL_R;
      ball.vx = Math.abs(ball.vx);
    } else if (ball.x > P_W - BALL_R) {
      ball.x = P_W - BALL_R;
      ball.vx = -Math.abs(ball.vx);
    }

    const bounce = (paddleX: number, upward: boolean) => {
      const rel = clamp((ball.x - (paddleX + PADDLE_W / 2)) / (PADDLE_W / 2), -1, 1);
      const speed = Math.min(9.5, Math.hypot(ball.vx, ball.vy) * 1.07 + 0.12);
      const angle = rel * 0.9; // angle depuis la verticale : effet « selon l'endroit »
      ball.vx = Math.sin(angle) * speed;
      ball.vy = (upward ? -1 : 1) * Math.abs(Math.cos(angle) * speed);
      rallyRef.current += 1;
      totalRef.current += 1;
      setRally(rallyRef.current);
    };

    // Raquette IA (haut) : la balle monte.
    if (
      ball.vy < 0 &&
      ball.y - BALL_R <= AI_Y + PADDLE_H &&
      ball.y - BALL_R >= AI_Y - 2 &&
      ball.x >= aiXRef.current - BALL_R &&
      ball.x <= aiXRef.current + PADDLE_W + BALL_R
    ) {
      ball.y = AI_Y + PADDLE_H + BALL_R;
      bounce(aiXRef.current, false);
    }

    // Raquette joueur (bas) : la balle descend.
    if (
      ball.vy > 0 &&
      ball.y + BALL_R >= PLAYER_Y &&
      ball.y + BALL_R <= PLAYER_Y + PADDLE_H + 2 &&
      ball.x >= playerXRef.current - BALL_R &&
      ball.x <= playerXRef.current + PADDLE_W + BALL_R
    ) {
      ball.y = PLAYER_Y - BALL_R;
      bounce(playerXRef.current, true);
    }

    // Points : balle sortie par le haut = FOH a raté → point joueur.
    if (ball.y < -BALL_R) point(true);
    else if (ball.y > P_H + BALL_R) point(false);

    draw();
  }, [draw, point, statusRef]);

  const reset = useCallback(() => {
    playerXRef.current = P_W / 2 - PADDLE_W / 2;
    aiXRef.current = P_W / 2 - PADDLE_W / 2;
    youRef.current = 0;
    fohRef.current = 0;
    totalRef.current = 0;
    rallyRef.current = 0;
    setYou(0);
    setFoh(0);
    setRally(0);
    serve(false);
    draw();
  }, [draw, serve]);

  const start = useCallback(() => {
    if (statusRef.current === 'paused') {
      setStatus('running');
      return;
    }
    reset();
    setStatus('running');
  }, [reset, setStatus, statusRef]);

  const toggle = useCallback(() => {
    if (statusRef.current === 'running') setStatus('paused');
    else start();
  }, [setStatus, start, statusRef]);

  const nudge = useCallback(
    (dir: number) => {
      playerXRef.current = clamp(playerXRef.current + dir * 28, 0, P_W - PADDLE_W);
      if (statusRef.current !== 'running') start();
      else draw();
    },
    [draw, start, statusRef],
  );

  useEffect(() => {
    reset();
    const id = window.setInterval(step, PONG_TICK);
    return () => window.clearInterval(id);
  }, [reset, step]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key === 'arrowleft' || key === 'a') {
        event.preventDefault();
        nudge(-1);
      } else if (key === 'arrowright' || key === 'd') {
        event.preventDefault();
        nudge(1);
      } else if (key === ' ') {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nudge, toggle]);

  /** Glisser du doigt / souris directement sur le canvas. */
  const movePaddleTo = (clientX: number) => {
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    if (rect.width === 0) return;
    const px = ((clientX - rect.left) / rect.width) * P_W;
    playerXRef.current = clamp(px - PADDLE_W / 2, 0, P_W - PADDLE_W);
    draw();
  };

  return (
    <section className="game-panel" data-game="pong">
      <ScoreBar
        score={rally}
        best={best}
        status={status}
        onToggle={toggle}
        onRestart={start}
        extra={`${t('games.you')} ${you} · ${t('games.foh')} ${foh}`}
      />

      <div className="game-stage">
        <canvas
          ref={canvasRef}
          width={P_W}
          height={P_H}
          className="game-canvas game-canvas-pong"
          role="img"
          aria-label={t('games.pong.title')}
          onPointerDown={(e) => movePaddleTo(e.clientX)}
          onPointerMove={(e) => {
            // Souris : on suit sans bouton ; tactile : uniquement doigt posé.
            if (e.pointerType !== 'touch' || e.buttons > 0) movePaddleTo(e.clientX);
          }}
        />
        {status === 'idle' && (
          <StageOverlay tone="info">
            <p className="game-overlay-title">{t('games.pong.title')}</p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.start')}
            </button>
          </StageOverlay>
        )}
        {status === 'paused' && (
          <StageOverlay tone="info">
            <p>{t('games.paused')}</p>
          </StageOverlay>
        )}
        {status === 'won' && (
          <StageOverlay tone="won">
            <p className="game-overlay-title">{t('games.youWin')}</p>
            <p>
              {t('games.you')} {you} · {t('games.foh')} {foh}
            </p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.restart')}
            </button>
          </StageOverlay>
        )}
        {status === 'over' && (
          <StageOverlay tone="over">
            <p className="game-overlay-title">{t('games.gameOver')}</p>
            <p>
              {t('games.you')} {you} · {t('games.foh')} {foh}
            </p>
            <button type="button" className="btn btn-primary game-btn" onClick={start}>
              {t('games.restart')}
            </button>
          </StageOverlay>
        )}
      </div>

      <p className="game-desc">{t('games.pong.desc')}</p>

      <div className="game-pad game-pad-pong" role="group" aria-label={t('games.pong.title')}>
        <button type="button" className="game-ctl game-ctl-wide" data-game-key="left" onClick={() => nudge(-1)}>
          <span aria-hidden="true">◀</span>
        </button>
        <button type="button" className="game-ctl game-ctl-wide" data-game-key="right" onClick={() => nudge(1)}>
          <span aria-hidden="true">▶</span>
        </button>
      </div>

      <p className="game-hint">{t('games.controlsHint')}</p>
    </section>
  );
}

/* ====================================================================== */
/*  Page                                                                  */
/* ====================================================================== */

const GAME_GROUPS: ReadonlyArray<{
  id: 'arcade' | 'toolbox';
  titleKey: string;
  tabs: ReadonlyArray<{ id: ArcadeTab; icon: string; titleKey: string }>;
}> = [
  {
    // 🕹️ ARCADE & SYNTHS — ce qu'on fait pour tuer le temps.
    id: 'arcade',
    titleKey: 'games.groups.arcade',
    tabs: [
      { id: 'snake', icon: '🐍', titleKey: 'games.snake.title' },
      { id: 'tetris', icon: '🧱', titleKey: 'games.tetris.title' },
      { id: 'pong', icon: '🏓', titleKey: 'games.pong.title' },
      { id: 'breaker', icon: '🧱', titleKey: 'games.breaker.title' },
      { id: 'runner', icon: '🚐', titleKey: 'games.runner.title' },
      { id: 'beatz', icon: '🎛️', titleKey: 'games.beatz.tab' },
      { id: 'kaoss', icon: '🎹', titleKey: 'games.kaoss.title' },
    ],
  },
  {
    // 🧰 STAGE TOOLBOX — ce qu'on fait pour que le concert sonne.
    id: 'toolbox',
    titleKey: 'games.groups.toolbox',
    tabs: [
      { id: 'tuner', icon: '🎸', titleKey: 'games.tuner.title' },
      { id: 'rta', icon: '📊', titleKey: 'games.rta.title' },
      { id: 'freqhunter', icon: '👂', titleKey: 'games.freqhunter.title' },
      { id: 'ld', icon: '💡', titleKey: 'games.ld.title' },
    ],
  },
];

/** Un seul module monté à la fois (boucle, micro et écouteurs s'arrêtent avec). */
function ActivePanel({ active }: { active: ArcadeTab }) {
  switch (active) {
    case 'snake':
      return <SnakeGame />;
    case 'tetris':
      return <TetrisGame />;
    case 'pong':
      return <PongGame />;
    case 'beatz':
      return <BandzBeatz />;
    case 'breaker':
      return <StageBreakerGame />;
    case 'runner':
      return <TourbusRunnerGame />;
    case 'kaoss':
      return <KaossSynthPanel />;
    case 'tuner':
      return <TunerMetronomePanel />;
    case 'rta':
      return <DbMeterRtaPanel />;
    case 'freqhunter':
      return <FreqHunterGame />;
    case 'ld':
      return <LdToolboxPanel />;
    default:
      return null;
  }
}

export default function GamesPage() {
  const { t } = useTranslation();
  const [active, setActive] = useState<ArcadeTab>('snake');

  return (
    <div className="page games-page" data-page="games" data-density="socle">
      <PageHeader title={withPageIcon('games.title', t('games.title'))} subtitle={t('games.subtitle')} />

      <div className="games-groups">
        {GAME_GROUPS.map((group) => (
          <div className="games-group" key={group.id} data-game-group={group.id}>
            <span className="games-group-title">{t(group.titleKey)}</span>
            <div className="games-tabs" role="tablist" aria-label={t(group.titleKey)}>
              {group.tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active === tab.id}
                  className={`games-tab${active === tab.id ? ' on' : ''}`}
                  data-game-tab={tab.id}
                  onClick={() => setActive(tab.id)}
                >
                  <span className="games-tab-ic" aria-hidden="true">
                    {tab.icon}
                  </span>
                  {t(tab.titleKey)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Un seul jeu/outil monté à la fois : sa boucle, son micro et ses
          écouteurs clavier s'arrêtent avec lui (pas de Pong ni de RTA qui
          tourne en arrière-plan). */}
      <ActivePanel active={active} />
    </div>
  );
}
